// src/components/AppointmentConstants.js
//
// Randevu durum sözlüğü ve şema üreticisi — TEK KAYNAK.
// Takvim, Randevu Listesi, Randevu Detay ve randevu modalları buradan okur;
// durum dizileri artık hiçbir ekranda elle tekrarlanmaz.
//
// Canlı veri notu (Faz 1 teşhisi): v1'den kalan kayıtlarda 'Iptal' (ASCII I),
// 'Geldi' ve 'Gelmedi' yazımları var; kod 'İptal' ürettiği için bu kayıtlar
// hiçbir filtreyle eşleşmiyordu. 72 kayıtta ise 'durum' alanı hiç yok.
// normalizeRandevuDurum tüm bu yazımları kanonik dört değere indirir.

import { Timestamp } from 'firebase/firestore';

export const RANDEVU_DURUMLARI = ['Bekleniyor', 'Onaylandı', 'Tamamlandı', 'İptal'];

export const VARSAYILAN_RANDEVU_DURUMU = 'Bekleniyor';

export const LEGACY_DURUM_HARITASI = {
    'Iptal': 'İptal',
    'iptal': 'İptal',
    'IPTAL': 'İptal',
    'Geldi': 'Tamamlandı',
    'Gelmedi': 'Tamamlandı',
    'Tamamlandi': 'Tamamlandı',
    'Onaylandi': 'Onaylandı',
    'Onayladi': 'Onaylandı'
};

export const normalizeRandevuDurum = (durum) => {
    if (!durum || typeof durum !== 'string') return VARSAYILAN_RANDEVU_DURUMU;
    const temiz = durum.trim();
    if (RANDEVU_DURUMLARI.includes(temiz)) return temiz;
    return LEGACY_DURUM_HARITASI[temiz] || VARSAYILAN_RANDEVU_DURUMU;
};

export const isIptalRandevu = (durum) => normalizeRandevuDurum(durum) === 'İptal';

// Kapanmış sayılan durumlar. Dashboard / İş Emirleri / appointmentSync bu diziyi
// AYRI AYRI yazıyordu; tek kaynak burasıdır.
export const KAPANMIS_RANDEVU_DURUMLARI = ['Tamamlandı', 'İptal'];

export const ACIK_RANDEVU_DURUMLARI = ['Bekleniyor', 'Onaylandı'];

export const isRandevuAcik = (durum) => ACIK_RANDEVU_DURUMLARI.includes(normalizeRandevuDurum(durum));

// Randevu kapanışının TEK otomatik yolu bağlı iş emrinin teslim edilmesidir
// (appointmentSync). İş emrine hiç dönüşmeyen randevuyu kapatan bir yol yoktu:
// bu kayıtlar Randevu Listesi'nin 'Bekleniyor' sekmesinde ve takvimde kırmızı
// "Geciken" olarak sonsuza dek kalıyordu (2026-08-30 sayımı: 14 kayıt, en eskisi
// 335 gün). "Süresi geçmiş" = durumu hâlâ açık VE bitiş (yoksa başlangıç) zamanı
// bugünden önce.
export const randevuSuresiGecti = (app, simdi = new Date()) => {
    if (!isRandevuAcik(app?.durum)) return false;
    const bitis = getRandevuEnd(app) || getRandevuStart(app);
    if (!bitis) return false;
    const gunBasi = new Date(simdi);
    gunBasi.setHours(0, 0, 0, 0);
    return bitis < gunBasi;
};

export const getRandevuDurumColor = (durum) => {
    const colors = {
        'Bekleniyor': 'info',
        'Onaylandı': 'success',
        'Tamamlandı': 'primary',
        'İptal': 'error'
    };
    return colors[normalizeRandevuDurum(durum)] || 'default';
};

export const randevuEtkinlikTurleri = [
    'Görüşme',
    'Müşteri Araç Teslim Alma',
    'Müşteri Araç Kontrol',
    'Müşteri Araç Teslimi'
];

// G2/G3: Paketler + Göçük Düzeltme emekli, PPF Full Body eklendi.
export const randevuHizmetTurleri = [
    'Cam Filmi', 'PPF Kaplama', 'PPF Full Body', 'Seramik Kaplama',
    'Detay Temizlik', 'Yıkama', 'Diğer'
];

const toDate = (value) => {
    if (!value) return null;
    try {
        const d = value.toDate ? value.toDate() : new Date(value);
        return isNaN(d.getTime()) ? null : d;
    } catch {
        return null;
    }
};

// Okuma tarafı geriye dönük uyum: yeni kayıtlar 'start' yazar,
// v1 kayıtlarının bir kısmı yalnızca 'randevuTarihi' taşıyor.
export const getRandevuStart = (app) => toDate(app?.start) || toDate(app?.randevuTarihi);

export const getRandevuEnd = (app) => toDate(app?.end);

const toTimestamp = (value) => {
    if (value instanceof Timestamp) return value;
    const d = toDate(value);
    return d ? Timestamp.fromDate(d) : null;
};

// Randevu şeması tek üreticiden çıkar. 'customerId' bilerek koşullu:
// canlıda 31 randevu boş string customerId taşıyor (RandevuOlusturModal'ın
// `musteriId || ''` yazımından) ve bu boş değer müşteri linklerini de
// eşitlik sorgularını da bozuyor. Müşteri bağlı değilse alan HİÇ yazılmaz.
export const buildAppointmentData = ({
    customerName,
    customerPhone,
    customerId,
    serviceType,
    eventType,
    notes,
    start,
    end,
    durum,
    ...extra
} = {}) => {
    const startTs = toTimestamp(start);
    const endTs = toTimestamp(end);
    if (!startTs || !endTs) {
        throw new Error('Randevu başlangıç ve bitiş zamanı zorunludur.');
    }

    const data = {
        // Faz 4.5: serbest metinler tamamı BÜYÜK HARF (tr-TR) — tüm randevu üreticileri
        // bu tek noktadan geçtiği için kural merkezî uygulanır.
        customerName: (customerName || '').toLocaleUpperCase('tr-TR'),
        customerPhone: customerPhone || '',
        serviceType: serviceType || '',
        eventType: eventType || 'Görüşme',
        notes: (notes || '').toLocaleUpperCase('tr-TR'),
        start: startTs,
        end: endTs,
        durum: normalizeRandevuDurum(durum),
        ...extra
    };

    if (customerId) data.customerId = customerId;
    return data;
};
